import { Link } from 'react-router-dom';
import { 
  ArrowRight, Check, ChevronRight, Building2, BarChart3,
  Smartphone, Zap, Database, Radar, Brain,
  AlertTriangle, Globe, Plane, Hotel,
  Heart, Trophy, Activity, CheckCircle, Warehouse,
  MapPin, Monitor, Shield, ShoppingCart, Truck, Stethoscope, HardHat,
  Calculator, Users, DollarSign, Briefcase, ClipboardCheck,
  Eye, Utensils, GraduationCap, Landmark
} from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import TabbedSection from '../components/TabbedSection';
import AccordionSection from '../components/AccordionSection';

const HomePage = () => {
  const { t, language } = useLanguage();
  const getLocalizedPath = (path) => `/${language}${path}`;

  // Icon mapping for how it works (4 steps)
  const stepIcons = {
    radar: Radar,
    database: Database,
    brain: Brain,
    checkCircle: CheckCircle
  };

  // Industry images for background
  const industryImages = [
    '/casino-gaming-floor.webp', // Casinos & Gaming
    '/industry_retail.jpg', // Retail
    'https://images.unsplash.com/photo-1771970777749-4478b9e74d09?w=800&q=80', // Airports
    '/industry_hotel.webp', // Hospitality
    'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=800&q=80', // Healthcare
    '/industry_stadium.webp',  // Venues
    '/industry-realestate.jpg',  // Real Estate
    '/restaurant-qsr-industry.png'  // Restaurant / QSR
  ];

  // Data from translations
  const howItWorksSteps = t('home.howItWorks.steps') || [];
  const operationalVisibilityFeatures = t('home.operationalVisibility.features') || [];
  const industryItems = t('home.industries.items') || [];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section with Product Visual */}
      <section className="relative pt-32 md:pt-40 pb-20 md:pb-28 overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 right-0 w-[600px] h-[600px] bg-gradient-to-br from-sky-100 to-blue-50 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-full max-w-96 h-96 bg-slate-100 rounded-full blur-3xl" />
        </div>
        
        <div className="container-max px-6 md:px-12 lg:px-28 relative">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center min-w-0">
            <div className="min-w-0">
              <h1 className="text-4xl md:text-5xl lg:text-[56px] font-extrabold text-slate-900 leading-[1.1] tracking-tight mb-6 max-w-[600px]" data-testid="hero-title">
                {t('home.hero.heroTitlePart1')}{' '}
                <span className="text-[#0077FF] whitespace-nowrap">{t('home.hero.heroTitleAccent')}</span>
              </h1>
              <p className="text-base text-slate-500 leading-relaxed mb-6 max-w-lg" data-testid="hero-supporting-text">
                {t('home.hero.heroSupportingText')}
              </p>

              {/* Zero-Friction Deployment Statement */}
              <div className="flex flex-wrap gap-3 sm:gap-5 mb-4" data-testid="hero-zero-friction">
                {['noApp', 'noLogin', 'noTraining'].map((key) => (
                  <div key={key} className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#0077FF] flex-shrink-0" />
                    <span className="text-sm sm:text-base font-bold text-slate-800 tracking-wide">
                      {t(`home.hero.${key}`)}
                    </span>
                  </div>
                ))}
              </div>
              <p className="text-sm text-slate-400 mb-8 max-w-lg" data-testid="hero-zero-friction-explainer">
                {t('home.hero.zeroFrictionExplainer')}
              </p>

              <div className="flex flex-wrap gap-4 mb-4">
                <Link to={getLocalizedPath('/contact')} className="inline-flex items-center justify-center gap-2 bg-[#FF7F00] hover:bg-[#E67200] text-white px-6 py-3 rounded-xl font-semibold text-base transition-all shadow-sm hover:shadow-md active:scale-[0.98]" data-testid="hero-cta">
                  {t('home.hero.ctaLabel')}
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link to={getLocalizedPath('/operational-risk')} className="inline-flex items-center gap-1.5 px-5 py-2.5 text-sm font-semibold text-[#0077FF] hover:text-[#0066DD] border border-[#0077FF]/30 hover:border-[#0077FF]/50 rounded-lg transition-colors" data-testid="hero-cta-blindspots">
                  <Eye className="w-4 h-4" />
                  {t('home.hero.ctaSecondaryLabel')}
                </Link>
              </div>
            </div>
            {/* Product Visual - Dashboard in Browser Frame */}
            <div className="relative min-w-0 overflow-hidden">
              <div 
                className="relative"
              >
                <div className="bg-slate-900 rounded-2xl p-1.5 shadow-2xl">
                  <div className="bg-slate-800 rounded-t-xl px-4 py-2.5 flex items-center gap-2 min-w-0">
                    <div className="flex gap-1.5">
                      <div className="w-3 h-3 rounded-full bg-red-500" />
                      <div className="w-3 h-3 rounded-full bg-yellow-500" />
                      <div className="w-3 h-3 rounded-full bg-green-500" />
                    </div>
                    <div className="flex-1 mx-3">
                      <div className="bg-slate-700 rounded-md px-3 py-1 text-slate-400 text-xs flex items-center gap-2">
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                        <span className="truncate">portal.tapelligence.com</span>
                      </div>
                    </div>
                  </div>
                  <div className="rounded-b-xl overflow-hidden">
                    <img
                      loading="eager" decoding="sync" src="/screenshot_dashboard.png"
                      alt="Tapelligence Dashboard"
                      className="w-full h-auto"
                    />
                  </div>
                </div>
                {/* Floating badge */}
                <div className="absolute -bottom-4 -left-4 bg-white rounded-xl shadow-lg p-3 border border-slate-100">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                      <Activity className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-xs text-emerald-600 font-medium">Live Monitoring</p>
                      <p className="text-sm font-semibold text-slate-900">Real-Time Data</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* When Detection is Delayed */}
      <section className="py-16 md:py-20 bg-white border-b border-slate-200" data-testid="detection-delayed-section">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8" data-testid="detection-delayed-title">When detection is delayed</h2>
            <div className="space-y-4 mb-10">
              {[
                'Hazards are discovered late — increasing safety and liability exposure',
                'Issues are routed inconsistently — delaying response and resolution',
                'Incidents are poorly documented — increasing claims complexity',
                'No real-time visibility across locations — limiting operational control',
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <AlertTriangle className="w-4 h-4 text-red-400 mt-1 flex-shrink-0" />
                  <p className="text-base text-slate-600">{item}</p>
                </div>
              ))}
            </div>
            <Link to={getLocalizedPath('/operational-risk#estimator-section')} className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#0077FF] hover:bg-[#0066DD] text-white text-base font-semibold rounded-xl transition-colors" data-testid="detection-delayed-calc-cta">
              <Calculator className="w-4 h-4" />
              Estimate Deployment
            </Link>
          </div>
        </div>
      </section>



      {/* Enterprise Scale Metrics Strip */}
      <section className="py-10 md:py-12 bg-slate-900" data-testid="scale-metrics-section">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {(t('home.scaleMetrics.items') || []).map((m, i) => {
              const icons = [Building2, Users, MapPin, Zap];
              const Ic = icons[i];
              return (
                <div key={i} className="text-center" data-testid={`scale-metric-${i}`}>
                  <Ic className="w-5 h-5 text-[#4DB8FF] mx-auto mb-2" />
                  <p className="text-2xl md:text-3xl font-bold text-white font-mono leading-none mb-1">{m.value}</p>
                  <p className="text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1">{m.title}</p>
                  <p className="text-[11px] text-[#4DB8FF]/60">{m.subtitle}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Tapelligence Definition Banner + Four-Pillar Framework */}
      <section className="py-16 md:py-20 bg-black" data-testid="definition-banner-section">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl mb-16" data-testid="definition-banner-content">
            <p className="text-xs md:text-sm tracking-[0.3em] uppercase text-slate-400 mb-8">
              A new category of operational intelligence
            </p>
            <h2 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-4 leading-[1.05]">
              <img src="/tapelligence_logo_transparent.png" alt="Tapelligence" className="h-[26px] w-auto" />
            </h2>
            <p className="text-lg md:text-xl text-slate-400 mb-1 font-light">/tap-EL-i-jens/ <span className="inline-block ml-1 opacity-50">&#x1F50A;</span></p>
            <p className="text-base text-slate-500 italic mb-6">noun</p>
            <div className="h-px bg-slate-700 mb-6"></div>
            <p className="text-xl md:text-2xl lg:text-3xl text-white leading-relaxed font-light">
              Operational intelligence derived from{' '}
              <span className="text-[#0077FF] font-medium">real-time hazard reporting</span>{' '}
              across complex physical environments.
            </p>
          </div>
          {/* Four-Pillar Framework */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="definition-pillars">
            {['connect', 'respond', 'record', 'intelligence'].map((pillar, index) => {
              const pillarIcons = [Users, Zap, Database, Brain];
              const pillarColors = [
                { bg: 'bg-[#0077FF]/10', border: 'border-[#0077FF]/20', icon: 'text-[#4DB8FF]', title: 'text-[#4DB8FF]' },
                { bg: 'bg-orange-500/10', border: 'border-orange-500/20', icon: 'text-orange-400', title: 'text-orange-400' },
                { bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', icon: 'text-emerald-400', title: 'text-emerald-400' },
                { bg: 'bg-violet-500/10', border: 'border-violet-500/20', icon: 'text-violet-400', title: 'text-violet-400' },
              ];
              const PillarIcon = pillarIcons[index];
              const c = pillarColors[index];
              const pillarData = t(`home.definitionPillars.${pillar}`) || {};
              return (
                <div key={pillar} className={`${c.bg} rounded-xl p-6 border ${c.border}`} data-testid={`pillar-${pillar}`}>
                  <div className="flex items-center gap-3 mb-4">
                    <PillarIcon className={`w-6 h-6 ${c.icon} flex-shrink-0`} />
                    <h3 className={`text-lg font-bold ${c.title} uppercase tracking-wider`}>{pillarData.title}</h3>
                  </div>
                  <p className="text-sm text-slate-400 leading-relaxed">{pillarData.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Enterprise-Ready Credibility Bar */}
      <section className="py-6 bg-slate-900" data-testid="enterprise-ready-section">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest text-center mb-4">{t('home.enterpriseReady.title')}</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {(t('home.enterpriseReady.items') || []).map((item, index) => (
              <div key={index} className="flex items-center gap-2.5 bg-slate-800/60 rounded-lg px-4 py-3 border border-slate-700/50" data-testid={`enterprise-ready-${index}`}>
                <div className="w-1.5 h-1.5 bg-[#0077FF] rounded-full flex-shrink-0" />
                <span className="text-sm text-slate-300 font-medium leading-tight">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Built for Real-World Operations — Social Proof */}
      <section className="py-14 bg-white" data-testid="social-proof-section">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <div className="text-center mb-10">
            <div className="w-12 h-0.5 bg-gradient-to-r from-[#0077FF] to-[#FF7F00] mx-auto mb-6" />
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3" data-testid="social-proof-title">
              Built for Real-World Operations
            </h2>
            <p className="text-base text-slate-500 max-w-2xl mx-auto">
              Tapelligence supports any environment where safety, accountability, and real-time awareness matter.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-x-6 gap-y-8">
            {[
              { icon: Utensils, label: 'Restaurants' },
              { icon: ShoppingCart, label: 'Retail' },
              { icon: Hotel, label: 'Hospitality' },
              { icon: Stethoscope, label: 'Healthcare' },
              { icon: GraduationCap, label: 'Education' },
              { icon: HardHat, label: 'Facilities' },
              { icon: Landmark, label: 'Municipal' },
              { icon: Truck, label: 'Transportation' },
              { icon: Plane, label: 'Airports' },
              { icon: Building2, label: 'Commercial Buildings' },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="flex flex-col items-center gap-2.5 py-3" data-testid={`social-proof-logo-${i}`}>
                  <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-[#0077FF]" />
                  </div>
                  <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider text-center">{item.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Real-Time Hazard Response Loop */}
      <section className="py-12 bg-white border-b border-slate-200" data-testid="operational-loop-section">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <div className="text-center max-w-4xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-8">
              {t('home.operationalLoop.title')}
            </h2>
            {/* Workflow Progression — Desktop: horizontal, Mobile: vertical */}
            <div className="hidden md:block mb-6" data-testid="workflow-desktop">
              {(() => {
                const stageStyles = [
                  { bg: '#FF7F00', ring: 'rgba(255,127,0,0.15)' },
                  { bg: '#F59E0B', ring: 'rgba(245,158,11,0.15)' },
                  { bg: '#0077FF', ring: 'rgba(0,119,255,0.15)' },
                  { bg: '#0EA5E9', ring: 'rgba(14,165,233,0.15)' },
                  { bg: '#22C55E', ring: 'rgba(34,197,94,0.15)' },
                ];
                const steps = t('home.operationalLoop.steps') || [];
                return (
                  <>
                    {/* Circles row — perfectly aligned */}
                    <div className="flex items-center justify-center gap-0">
                      {steps.map((step, index) => {
                        const style = stageStyles[index] || stageStyles[0];
                        return (
                          <div key={index} className="flex items-center" data-testid={`loop-step-${index}`}>
                            <div
                              className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold shadow-sm flex-shrink-0"
                              style={{ backgroundColor: style.bg, boxShadow: `0 0 0 4px ${style.ring}` }}
                            >
                              {index + 1}
                            </div>
                            {index < steps.length - 1 && (
                              <div className="flex items-center mx-3">
                                <div className="w-10 h-[2px] bg-slate-200" />
                                <ChevronRight className="w-4 h-4 text-slate-300 -ml-1" />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {/* Labels row — below circles, evenly spaced */}
                    <div className="flex justify-center gap-0 mt-2">
                      {steps.map((step, index) => (
                        <div key={index} className="flex items-start">
                          <span className="text-xs font-semibold text-slate-700 text-center leading-tight w-[68px]">
                            {step}
                          </span>
                          {index < steps.length - 1 && <div className="w-[53px] flex-shrink-0" />}
                        </div>
                      ))}
                    </div>
                  </>
                );
              })()}
            </div>
            {/* Mobile: vertical stack — circles aligned in a straight column */}
            <div className="md:hidden flex flex-col items-center mb-6" data-testid="workflow-mobile">
              <div className="inline-flex flex-col">
                {(t('home.operationalLoop.steps') || []).map((step, index, arr) => {
                  const stageStyles = [
                    { bg: '#FF7F00', ring: 'rgba(255,127,0,0.15)' },
                    { bg: '#F59E0B', ring: 'rgba(245,158,11,0.15)' },
                    { bg: '#0077FF', ring: 'rgba(0,119,255,0.15)' },
                    { bg: '#0EA5E9', ring: 'rgba(14,165,233,0.15)' },
                    { bg: '#22C55E', ring: 'rgba(34,197,94,0.15)' },
                  ];
                  const style = stageStyles[index] || stageStyles[0];
                  return (
                    <div key={index} className="flex flex-col" data-testid={`loop-step-mobile-${index}`}>
                      <div className="flex items-center gap-3">
                        <div
                          className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                          style={{ backgroundColor: style.bg, boxShadow: `0 0 0 3px ${style.ring}` }}
                        >
                          {index + 1}
                        </div>
                        <span className="text-sm font-semibold text-slate-700">
                          {step}
                        </span>
                      </div>
                      {index < arr.length - 1 && (
                        <div className="w-[2px] h-4 my-1 ml-[17px]" style={{ backgroundColor: style.bg }} />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            <p className="text-base text-slate-600 leading-relaxed max-w-2xl mx-auto">
              {t('home.operationalLoop.description')}
            </p>
          </div>
        </div>
      </section>

      {/* The Missing Layer in Physical Operations */}
      <section className="section-padding bg-white" data-testid="missing-layer-section">
        <div className="container-max">
          <div className="text-center max-w-4xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              {t('home.missingLayer.title')}
            </h2>
            <p className="text-xl text-slate-600 mb-8">
              {t('home.missingLayer.subtitle')}
            </p>
            <div className="space-y-4 text-left md:text-center">
              {(t('home.missingLayer.body') || []).map((paragraph, index) => (
                <p 
                  key={index} 
                  className={`text-lg leading-relaxed ${
                    index === 0 ? 'text-slate-800 font-semibold' : 
                    index === (t('home.missingLayer.body') || []).length - 1 ? 'text-[#0077FF] font-medium' : 
                    'text-slate-600'
                  }`}
                >
                  {paragraph}
                </p>
              ))}
            </div>
          </div>
          
          {/* Three Column Layout */}
          <div className="grid md:grid-cols-3 gap-8">
            {(t('home.missingLayer.columns') || []).map((column, index) => {
              const icons = [AlertTriangle, Smartphone, BarChart3];
              const IconComponent = icons[index] || AlertTriangle;
              const colors = [
                { bg: 'bg-red-50', iconBg: 'bg-red-100', text: 'text-red-600', border: 'border-red-200' },
                { bg: 'bg-[#0077FF]/5', iconBg: 'bg-[#0077FF]/10', text: 'text-[#0077FF]', border: 'border-[#0077FF]/20' },
                { bg: 'bg-emerald-50', iconBg: 'bg-emerald-100', text: 'text-emerald-600', border: 'border-emerald-200' }
              ];
              const color = colors[index];
              
              return (
                <div key={index} className={`${color.bg} rounded-2xl p-8 border ${color.border} text-center`}>
                  <div className={`w-16 h-16 ${color.iconBg} rounded-2xl flex items-center justify-center mx-auto mb-6`}>
                    <IconComponent className={`w-8 h-8 ${color.text}`} />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{column.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{column.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Now Section — Progressive Disclosure */}
      <section className="section-padding bg-slate-50" data-testid="why-now-section">
        <div className="container-max">
          <AccordionSection
            testId="why-now-accordion"
            expandLabel={t('home.accordion.showDetails')}
            collapseLabel={t('home.accordion.hideDetails')}
            visibleContent={
              <div className="text-center max-w-4xl mx-auto">
                <span className="text-caption text-[#0077FF] mb-4 block">Market Timing</span>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                  {t('home.whyNow.title')}
                </h2>
                <p className="text-lg text-slate-600 leading-relaxed">
                  {t('home.whyNow.subtitle')}
                </p>
              </div>
            }
            expandableContent={
              <div className="mt-10">
                <div className="grid md:grid-cols-3 gap-8 mb-12">
                  {(t('home.whyNow.columns') || []).map((column, index) => {
                    const icons = [Smartphone, Building2, BarChart3];
                    const IconComponent = icons[index] || Smartphone;
                    const colors = [
                      { bg: 'bg-[#0077FF]/5', iconBg: 'bg-[#0077FF]/10', text: 'text-[#0077FF]', border: 'border-[#0077FF]/20' },
                      { bg: 'bg-amber-50', iconBg: 'bg-amber-100', text: 'text-amber-600', border: 'border-amber-200' },
                      { bg: 'bg-emerald-50', iconBg: 'bg-emerald-100', text: 'text-emerald-600', border: 'border-emerald-200' }
                    ];
                    const color = colors[index];
                    return (
                      <div key={index} className={`${color.bg} rounded-2xl p-8 border ${color.border}`}>
                        <div className={`w-14 h-14 ${color.iconBg} rounded-xl flex items-center justify-center mb-6`}>
                          <IconComponent className={`w-7 h-7 ${color.text}`} />
                        </div>
                        <h3 className="text-xl font-bold text-slate-900 mb-3">{column.title}</h3>
                        <p className="text-slate-600 leading-relaxed">{column.text}</p>
                      </div>
                    );
                  })}
                </div>
                <div className="text-center">
                  <p className="text-lg md:text-xl font-medium text-slate-800">
                    {t('home.whyNow.finalLine')}
                  </p>
                </div>
              </div>
            }
          />
        </div>
      </section>



      {/* How Tapelligence Works - 4 Steps */}
      <section className="section-padding bg-slate-50" data-testid="how-it-works-section">
        <div className="container-max">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="w-10 h-0.5 bg-[#FF7F00] mx-auto mb-4" />
            <span className="text-caption text-[#0077FF] mb-4 block">Process</span>
            <h2 className="text-h2 text-slate-900">
              {t('home.howItWorks.title')}
            </h2>
          </div>
          
          <div className="grid md:grid-cols-4 gap-8">
            {howItWorksSteps.map((step, index) => {
              const IconComponent = stepIcons[step.icon] || Radar;
              const colors = [
                { bg: 'bg-[#0077FF]', light: 'bg-[#0077FF]/10', text: 'text-[#0077FF]' },
                { bg: 'bg-emerald-500', light: 'bg-emerald-100', text: 'text-emerald-600' },
                { bg: 'bg-amber-500', light: 'bg-amber-100', text: 'text-amber-600' },
                { bg: 'bg-violet-500', light: 'bg-violet-100', text: 'text-violet-600' }
              ];
              const color = colors[index % colors.length];
              
              return (
                <div key={index} className="relative">
                  <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm hover:shadow-lg transition-all h-full">
                    <div className={`w-16 h-16 ${color.light} rounded-2xl flex items-center justify-center mb-6`}>
                      <IconComponent className={`w-8 h-8 ${color.text}`} />
                    </div>
                    <div className={`inline-block px-3 py-1 ${color.bg} text-white text-xs font-bold rounded-full mb-4`}>
                      STEP {step.number}
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-3">{step.title}</h3>
                    <p className="text-slate-600 leading-relaxed">{step.description}</p>
                  </div>
                  {index < howItWorksSteps.length - 1 && (
                    <div className="hidden md:flex absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                      <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center">
                        <ChevronRight className="w-5 h-5 text-slate-500" />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Engine Tags in Action */}
      <section className="section-padding bg-slate-900" data-testid="engine-tags-section">
        <div className="container-max">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <span className="text-caption text-[#4DB8FF] mb-4 block">Product in Action</span>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                {t('home.engineTagsInAction.title')}
              </h2>
              <p className="text-lg text-slate-300 mb-8">
                {t('home.engineTagsInAction.subtitle')}
              </p>
              <ul className="space-y-4">
                {(t('home.engineTagsInAction.features') || []).map((feature, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-[#0077FF]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Check className="w-5 h-5 text-[#4DB8FF]" />
                    </div>
                    <span className="text-slate-200 font-medium">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative group">
              <img
                loading="eager" decoding="sync" src="/engine_tags_in_action.png"
                alt="Tapelligence Engine Tags deployed across environments"
                className="rounded-2xl shadow-2xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Enterprise Command Center */}
      <section className="section-padding bg-white" data-testid="command-center-section">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center min-w-0">
            <div className="min-w-0">
              <span className="text-caption text-[#0077FF] mb-4 block">Command & Control</span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-8">
                {t('home.commandCenterIntro.title')}
              </h2>
              <div className="space-y-5 text-lg text-slate-600 leading-relaxed">
                <p>{t('home.commandCenterIntro.text1')}</p>
                <p>{t('home.commandCenterIntro.text2')}</p>
              </div>
            </div>
            <div className="relative group min-w-0 overflow-hidden">
              <div className="bg-slate-900 rounded-2xl p-1.5 shadow-2xl">
                <div className="bg-slate-800 rounded-t-xl px-4 py-2.5 flex items-center gap-2 min-w-0">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-500" />
                    <div className="w-3 h-3 rounded-full bg-yellow-500" />
                    <div className="w-3 h-3 rounded-full bg-green-500" />
                  </div>
                  <div className="flex-1 mx-3">
                    <div className="bg-slate-700 rounded-md px-3 py-1 text-slate-400 text-xs flex items-center gap-2">
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                      <span className="truncate">portal.tapelligence.com/command</span>
                    </div>
                  </div>
                </div>
                <div className="rounded-b-xl overflow-hidden">
                  <img
                    loading="eager" decoding="sync" src="/screenshot_command_center.png"
                    alt="Tapelligence Command Center Dashboard"
                    className="w-full h-auto"
                    data-testid="command-center-dashboard-img"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Operational Record & Incident Defensibility — Progressive Disclosure */}
      <section className="section-padding bg-slate-50 border-y border-slate-200" data-testid="defensibility-section">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl">
            <AccordionSection
              testId="defensibility-accordion"
              expandLabel={t('home.accordion.showDetails')}
              collapseLabel={t('home.accordion.hideDetails')}
              visibleContent={
                <>
                  <span className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3 block">Operational Record</span>
                  <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4" data-testid="defensibility-title">Operational Record &amp; Incident Defensibility</h2>
                  <p className="text-base text-slate-600 leading-relaxed">
                    Every incident is captured as a verifiable record — time-stamped, location-specific, and logged with response context.
                  </p>
                </>
              }
              expandableContent={
                <div className="mt-4">
                  <p className="text-base text-slate-500 leading-relaxed mb-8">
                    Creating a consistent audit trail of detection and response — so teams can show not just what happened, but how quickly it was handled.
                  </p>
                  <div className="space-y-3">
                    {[
                      'Time-stamped detection and response tracking',
                      'Structured incident records across locations',
                      'Clear visibility into response times and actions',
                      'Supports internal accountability and external defensibility',
                    ].map((item, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-slate-700">{item}</p>
                      </div>
                    ))}
                  </div>
                </div>
              }
            />
          </div>
        </div>
      </section>

      {/* Platform Capabilities — Tabbed Experience */}
      <section className="section-padding bg-white" data-testid="operational-visibility-section-content">
        <div className="container-max">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <span className="text-caption text-[#0077FF] mb-4 block">Platform Capabilities</span>
            <h2 className="text-h2 text-slate-900 mb-4">
              {t('home.operationalVisibility.title')}
            </h2>
          </div>

          <TabbedSection
            testId="capabilities-tabs"
            tabListClassName="mx-auto mb-8"
            tabs={[
              {
                id: 'visibility',
                label: 'Visibility',
                content: (
                  <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                    <div>
                      <p className="text-body-lg mb-8">
                        {t('home.operationalVisibility.subtitle')}
                      </p>
                      <ul className="space-y-4">
                        {operationalVisibilityFeatures.map((feature, index) => (
                          <li key={index} className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-[#0077FF]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                              <Check className="w-5 h-5 text-[#0077FF]" />
                            </div>
                            <span className="text-slate-700 font-medium">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500 mb-3" data-testid="zone-context-above">
                        This is what your operation looks like. Here's what's hard to see in real time.
                      </p>
                      <img
                        loading="eager" decoding="sync" src="/screenshot_laptop_dashboard.png"
                        alt="Tapelligence Command Center on Laptop"
                        className="w-full h-auto rounded-2xl shadow-2xl"
                        data-testid="operational-visibility-img"
                      />
                      <p className="text-sm text-slate-400 mt-3" data-testid="zone-context-below">
                        Most incidents aren't missed — they're discovered too late, routed inconsistently, and poorly documented.
                      </p>
                    </div>
                  </div>
                ),
              },
              {
                id: 'incident-management',
                label: 'Incident Management',
                content: (
                  <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center min-w-0" data-testid="incident-management-section">
                    <div className="order-2 lg:order-1 relative group min-w-0 overflow-hidden">
                      <p className="text-sm text-slate-500 mb-3" data-testid="dashboard-defensibility-trigger">
                        Most teams can show what was done. Fewer can prove when it was first known — and how quickly it was handled.
                      </p>
                      <div className="bg-slate-900 rounded-2xl p-2 shadow-2xl">
                        <div className="bg-slate-800 rounded-t-lg px-4 py-3 flex items-center gap-2 min-w-0">
                          <div className="flex gap-2">
                            <div className="w-3 h-3 rounded-full bg-red-500" />
                            <div className="w-3 h-3 rounded-full bg-yellow-500" />
                            <div className="w-3 h-3 rounded-full bg-green-500" />
                          </div>
                          <div className="flex-1 mx-4">
                            <div className="bg-slate-700 rounded-md px-3 py-1.5 text-slate-400 text-xs truncate">
                              portal.tapelligence.com/analytics
                            </div>
                          </div>
                        </div>
                        <div className="rounded-b-lg overflow-hidden">
                          <img
                            loading="eager" decoding="sync" src="/screenshot_analytics.png"
                            alt="Incident Analytics Dashboard"
                            className="w-full h-auto"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="order-1 lg:order-2">
                      <span className="text-caption text-emerald-600 mb-4 block">Incident Intelligence</span>
                      <h2 className="text-h2 text-slate-900 mb-4">
                        {t('home.incidentManagement.title')}
                      </h2>
                      <p className="text-body-lg mb-4">
                        {t('home.incidentManagement.description')}
                      </p>
                      <p className="text-sm text-slate-500 mb-4">
                        Identify repeat issues, high-risk zones, and response-time gaps across locations.
                      </p>
                      <div className="mb-6">
                        <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">What this data reveals:</p>
                        <div className="space-y-2">
                          {[
                            'High-risk zones across locations',
                            'Response time gaps between teams',
                            'Repeat incident hotspots',
                            'Operational inconsistencies across facilities',
                          ].map((item, i) => (
                            <div key={i} className="flex items-center gap-2">
                              <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full flex-shrink-0" />
                              <span className="text-sm text-slate-600">{item}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        {['Pattern Recognition', 'Priority Ranking', 'Response Tracking', 'Trend Analysis'].map((item, index) => (
                          <div key={index} className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-emerald-500 rounded-full" />
                            <span className="text-slate-700 text-sm font-medium">{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ),
              },
              {
                id: 'operational-view',
                label: 'Operational View',
                content: (
                  <div className="px-0 md:px-6 lg:px-12" data-testid="operational-view-section">
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-6 text-center">Sample Operational View</p>
                    <div className="bg-white rounded-xl border border-slate-200 p-6 md:p-8">
                      <div className="grid md:grid-cols-3 gap-8">
                        <div>
                          <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4">Top Hazard Locations</h4>
                          <div className="space-y-3">
                            {[
                              { zone: 'Entrance A — Main Lobby', pct: 78 },
                              { zone: 'Restroom Corridor — L2', pct: 64 },
                              { zone: 'Food Court — West', pct: 52 },
                              { zone: 'Loading Dock — B3', pct: 41 },
                              { zone: 'Parking Structure — P1', pct: 28 },
                            ].map((h, i) => (
                              <div key={i}>
                                <div className="flex justify-between text-xs mb-1">
                                  <span className="text-slate-600 truncate mr-2">{h.zone}</span>
                                  <span className="text-slate-400 flex-shrink-0">{h.pct}%</span>
                                </div>
                                <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                                  <div className={`h-full rounded-full ${h.pct > 60 ? 'bg-red-400' : h.pct > 40 ? 'bg-amber-400' : 'bg-emerald-400'}`} style={{ width: `${h.pct}%` }} />
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div>
                          <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4">Response Time by Zone</h4>
                          <div className="space-y-3">
                            {[
                              { zone: 'Terminal 1', time: '2.4 min', color: 'bg-emerald-400' },
                              { zone: 'Terminal 2', time: '3.1 min', color: 'bg-emerald-400' },
                              { zone: 'Food Court', time: '4.8 min', color: 'bg-amber-400' },
                              { zone: 'Parking', time: '6.2 min', color: 'bg-amber-400' },
                              { zone: 'Loading Dock', time: '8.7 min', color: 'bg-red-400' },
                            ].map((z, i) => (
                              <div key={i} className="flex items-center gap-3">
                                <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${z.color}`} />
                                <span className="text-xs text-slate-600 flex-1 truncate">{z.zone}</span>
                                <span className="text-xs font-semibold text-slate-700">{z.time}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div>
                          <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4">Location Comparison</h4>
                          <div className="space-y-3">
                            {[
                              { loc: 'Store #102 — Austin', score: 92, trend: '+12%' },
                              { loc: 'Store #87 — Denver', score: 84, trend: '+8%' },
                              { loc: 'Store #215 — Miami', score: 71, trend: '+4%' },
                              { loc: 'Store #43 — Chicago', score: 63, trend: '-2%' },
                              { loc: 'Store #156 — Seattle', score: 58, trend: '+1%' },
                            ].map((l, i) => (
                              <div key={i} className="flex items-center gap-3">
                                <span className="text-xs text-slate-600 flex-1 truncate">{l.loc}</span>
                                <span className={`text-xs font-semibold ${l.score >= 80 ? 'text-emerald-600' : l.score >= 65 ? 'text-amber-600' : 'text-red-500'}`}>{l.score}</span>
                                <span className={`text-[10px] ${l.trend.startsWith('+') ? 'text-emerald-500' : 'text-red-400'}`}>{l.trend}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ),
              },
            ]}
          />
        </div>
      </section>

      {/* Designed for High-Traffic Physical Environments */}
      <section className="bg-slate-950" style={{ paddingTop: '100px', paddingBottom: '100px' }} data-testid="high-traffic-section">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="text-center max-w-[700px] mx-auto mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">{t('home.highTraffic.title')}</h2>
            <p className="text-base text-slate-400">{t('home.highTraffic.subtitle')}</p>
          </div>

          {/* Row 1 */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-y-10" style={{ gap: '70px', justifyItems: 'center', alignItems: 'center' }}>
            <svg viewBox="0 0 200 55" className="h-10 md:h-12 w-auto opacity-50"><text x="100" y="28" textAnchor="middle" fill="#cbd5e1" fontSize="24" fontWeight="900" fontFamily="'Arial Black',sans-serif" letterSpacing="1">COSTCO</text><text x="100" y="46" textAnchor="middle" fill="#94a3b8" fontSize="10" fontWeight="600" fontFamily="system-ui" letterSpacing="3">WHOLESALE</text></svg>
            <svg viewBox="0 0 200 50" className="h-10 md:h-12 w-auto opacity-50"><text x="80" y="32" textAnchor="middle" fill="#cbd5e1" fontSize="26" fontWeight="700" fontFamily="system-ui">Walmart</text><text x="165" y="24" textAnchor="middle" fill="#94a3b8" fontSize="22">✳</text></svg>
            <svg viewBox="0 0 220 50" className="h-10 md:h-12 w-auto opacity-50"><text x="110" y="32" textAnchor="middle" fill="#cbd5e1" fontSize="18" fontWeight="700" fontFamily="system-ui" letterSpacing="2">MGM RESORTS</text></svg>
            <svg viewBox="0 0 220 55" className="h-10 md:h-12 w-auto opacity-50"><text x="110" y="30" textAnchor="middle" fill="#cbd5e1" fontSize="18" fontWeight="700" fontFamily="system-ui" letterSpacing="3">CAESARS</text><text x="110" y="46" textAnchor="middle" fill="#94a3b8" fontSize="8" fontWeight="600" fontFamily="system-ui" letterSpacing="4">ENTERTAINMENT</text></svg>
          </div>

          <div className="border-t border-slate-800/40 my-10" />

          {/* Row 2 */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-y-10" style={{ gap: '70px', justifyItems: 'center', alignItems: 'center' }}>
            <svg viewBox="0 0 180 60" className="h-10 md:h-12 w-auto opacity-50"><text x="90" y="28" textAnchor="middle" fill="#cbd5e1" fontSize="32" fontWeight="300" fontFamily="'Georgia',serif">M</text><text x="90" y="50" textAnchor="middle" fill="#94a3b8" fontSize="10" fontWeight="600" fontFamily="system-ui" letterSpacing="4">MARRIOTT</text></svg>
            <svg viewBox="0 0 160 50" className="h-10 md:h-12 w-auto opacity-50"><text x="80" y="34" textAnchor="middle" fill="#cbd5e1" fontSize="28" fontWeight="700" fontFamily="'Georgia',serif">Hilton</text></svg>
            <svg viewBox="0 0 260 50" className="h-10 md:h-12 w-auto opacity-50"><text x="80" y="20" textAnchor="middle" fill="#94a3b8" fontSize="9" fontWeight="600" fontFamily="system-ui" letterSpacing="1">AÉROPORTS</text><text x="80" y="32" textAnchor="middle" fill="#94a3b8" fontSize="9" fontWeight="600" fontFamily="system-ui" letterSpacing="1">DE MONTRÉAL</text><text x="185" y="30" textAnchor="middle" fill="#cbd5e1" fontSize="24" fontWeight="700" fontFamily="system-ui">YUL</text></svg>
            <svg viewBox="0 0 220 55" className="h-10 md:h-12 w-auto opacity-50"><text x="110" y="30" textAnchor="middle" fill="#cbd5e1" fontSize="17" fontWeight="500" fontFamily="system-ui">Toronto Pearson</text></svg>
          </div>

          <div className="border-t border-slate-800/40 my-10" />

          {/* Row 3 */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-10 max-w-[900px] mx-auto" style={{ gap: '70px', justifyItems: 'center', alignItems: 'center' }}>
            <svg viewBox="0 0 140 50" className="h-10 md:h-12 w-auto opacity-50"><text x="70" y="36" textAnchor="middle" fill="#cbd5e1" fontSize="36" fontWeight="900" fontFamily="'Arial Black',sans-serif" fontStyle="italic">LAX</text></svg>
            <svg viewBox="0 0 260 55" className="h-10 md:h-12 w-auto opacity-50"><text x="130" y="28" textAnchor="middle" fill="#cbd5e1" fontSize="16" fontWeight="700" fontFamily="system-ui" letterSpacing="2">CHASE STADIUM</text><text x="130" y="45" textAnchor="middle" fill="#94a3b8" fontSize="11" fontWeight="600" fontFamily="system-ui" letterSpacing="4">ARENA</text></svg>
            <svg viewBox="0 0 240 55" className="h-10 md:h-12 w-auto opacity-50"><text x="120" y="28" textAnchor="middle" fill="#cbd5e1" fontSize="20" fontWeight="600" fontFamily="system-ui">crypto.com</text><text x="120" y="46" textAnchor="middle" fill="#94a3b8" fontSize="12" fontWeight="600" fontFamily="system-ui" letterSpacing="5">ARENA</text></svg>
          </div>

          <p className="text-xs text-slate-500 text-center mt-12 italic opacity-60">{t('home.highTraffic.disclaimer')}</p>

          {/* Tapelligence Logo */}
          <div className="flex justify-center mt-16">
            <img loading="eager" decoding="sync" src="/tapelligence_logo_transparent.png" alt="Tapelligence" className="h-[46px] md:h-[55px] w-auto" />
          </div>
        </div>
      </section>

      {/* Built for Complex Physical Operations - Industries */}
      <section className="section-padding bg-slate-900" data-testid="industries-section">
        <div className="container-max">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-caption text-[#4DB8FF] mb-4 block">Industries</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {t('home.industries.title')}
            </h2>
            <p className="text-lg text-slate-300">
              {t('home.industries.subtitle')}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {industryItems.map((industry, index) => (
              <div 
                key={index} 
                className="relative group overflow-hidden rounded-xl h-64"
              >
                <div 
                  className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                  style={{ backgroundImage: `url(${industryImages[index]})` }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/70 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="text-lg font-bold text-white leading-snug">{industry.title}</h3>
                  {industry.supporting && (
                    <p className="text-xs text-slate-300 mt-1">{industry.supporting}</p>
                  )}
                </div>
                <div className="absolute inset-0 flex items-end p-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-slate-900/80">
                  <div>
                    <h3 className="text-lg font-bold text-white mb-2">{industry.title}</h3>
                    <p className="text-sm text-slate-300">{industry.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Deployment Scenarios — Tabbed by Vertical */}
      <section className="section-padding bg-white" data-testid="deployment-scenarios-section">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <span className="text-caption text-[#0077FF] mb-4 block">Use Cases</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              {t('home.deploymentScenarios.title')}
            </h2>
            <p className="text-lg text-slate-600">
              {t('home.deploymentScenarios.subtitle')}
            </p>
          </div>
          <TabbedSection
            testId="deployment-tabs"
            tabListClassName="mx-auto mb-8"
            tabs={(t('home.deploymentScenarios.cards') || []).map((card, index) => {
              const scenarioIcons = [Warehouse, Plane, Hotel, Building2];
              const scenarioColors = [
                { bg: 'bg-[#0077FF]/5', iconBg: 'bg-[#0077FF]/10', text: 'text-[#0077FF]', border: 'border-[#0077FF]/20' },
                { bg: 'bg-slate-50', iconBg: 'bg-slate-100', text: 'text-slate-600', border: 'border-slate-200' },
                { bg: 'bg-amber-50', iconBg: 'bg-amber-100', text: 'text-amber-600', border: 'border-amber-200' },
                { bg: 'bg-emerald-50', iconBg: 'bg-emerald-100', text: 'text-emerald-600', border: 'border-emerald-200' }
              ];
              const tabLabels = ['Retail', 'Airports', 'Hospitality', 'Warehouses'];
              const IconComponent = scenarioIcons[index] || Building2;
              const color = scenarioColors[index];
              return {
                id: tabLabels[index].toLowerCase(),
                label: tabLabels[index],
                icon: IconComponent,
                content: (
                  <div className={`${color.bg} rounded-2xl p-8 border ${color.border} max-w-2xl mx-auto`}>
                    <div className="flex items-center gap-4 mb-5">
                      <div className={`w-14 h-14 ${color.iconBg} rounded-xl flex items-center justify-center flex-shrink-0`}>
                        <IconComponent className={`w-7 h-7 ${color.text}`} />
                      </div>
                      <h3 className="text-xl font-bold text-slate-900">{card.title}</h3>
                    </div>
                    <p className="text-slate-600 leading-relaxed mb-5">{card.description}</p>
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">{t('home.deploymentScenarios.exampleLabel')}</p>
                      <div className="flex flex-wrap gap-2">
                        {(card.examples || []).map((ex, ei) => (
                          <span key={ei} className="text-sm bg-white px-3 py-1.5 rounded-lg border border-slate-200 text-slate-700">{ex}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                ),
              };
            })}
          />
        </div>
      </section>

      {/* Operational Intelligence at Scale */}
      <section className="py-16 bg-slate-900" data-testid="ops-at-scale-section">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <div className="text-center mb-10">
            <span className="text-caption text-[#4DB8FF] mb-4 block">{t('home.opsAtScale.label')}</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white">
              {t('home.opsAtScale.title')}
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {(t('home.opsAtScale.metrics') || []).map((metric, index) => {
              const scaleIcons = [Building2, Globe, Activity, Zap];
              const ScaleIcon = scaleIcons[index] || Activity;
              return (
                <div key={index} className="text-center p-6 bg-slate-800/60 rounded-xl border border-slate-700">
                  <div className="w-12 h-12 mx-auto mb-4 bg-[#0077FF]/20 rounded-xl flex items-center justify-center">
                    <ScaleIcon className="w-6 h-6 text-[#4DB8FF]" />
                  </div>
                  <p className="text-3xl md:text-4xl font-bold text-white mb-1">{metric.value}</p>
                  <p className="text-[#4DB8FF] font-medium text-sm mb-2">{metric.label}</p>
                  <p className="text-slate-400 text-xs leading-relaxed">{metric.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Operational Impact at Scale */}
      <section className="section-padding bg-slate-50" data-testid="outcomes-section">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-caption text-[#0077FF] mb-4 block">{t('home.outcomes.label')}</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              {t('home.outcomes.title')}
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              {t('home.outcomes.description')}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {(() => {
              const impactIcons = [Zap, Monitor, AlertTriangle, Shield, Database, Globe];
              const impactColors = [
                { bg: 'bg-[#0077FF]/5', iconBg: 'bg-[#0077FF]/10', text: 'text-[#0077FF]', border: 'border-[#0077FF]/20' },
                { bg: 'bg-emerald-50', iconBg: 'bg-emerald-100', text: 'text-emerald-600', border: 'border-emerald-200' },
                { bg: 'bg-amber-50', iconBg: 'bg-amber-100', text: 'text-amber-600', border: 'border-amber-200' },
                { bg: 'bg-rose-50', iconBg: 'bg-rose-100', text: 'text-rose-600', border: 'border-rose-200' },
                { bg: 'bg-violet-50', iconBg: 'bg-violet-100', text: 'text-violet-600', border: 'border-violet-200' },
                { bg: 'bg-slate-50', iconBg: 'bg-slate-200', text: 'text-slate-600', border: 'border-slate-300' }
              ];
              return (t('home.outcomes.cards') || []).map((card, index) => {
                const ImpactIcon = impactIcons[index] || Zap;
                const c = impactColors[index];
                return (
                  <div key={index} className={`${c.bg} rounded-2xl p-8 border ${c.border} hover:shadow-lg transition-all`} data-testid={`impact-card-${index}`}>
                    <div className={`w-14 h-14 ${c.iconBg} rounded-xl flex items-center justify-center mb-6`}>
                      <ImpactIcon className={`w-7 h-7 ${c.text}`} />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-3">{card.title}</h3>
                    <p className="text-slate-600 leading-relaxed">{card.description}</p>
                  </div>
                );
              });
            })()}
          </div>
        </div>
      </section>

      {/* Designed for Operational Leaders — Progressive Disclosure */}
      <section className="section-padding bg-slate-50" data-testid="operational-leaders-section">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <AccordionSection
            testId="operational-leaders-accordion"
            expandLabel={t('home.accordion.showDetails')}
            collapseLabel={t('home.accordion.hideDetails')}
            visibleContent={
              <div className="text-center max-w-3xl mx-auto">
                <span className="text-caption text-[#0077FF] mb-4 block">{t('home.operationalLeaders.label')}</span>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                  {t('home.operationalLeaders.title')}
                </h2>
                <p className="text-lg text-slate-600 leading-relaxed mb-3">
                  {t('home.operationalLeaders.intro1')}
                </p>
                <p className="text-lg text-slate-600 leading-relaxed">
                  {t('home.operationalLeaders.intro2')}
                </p>
              </div>
            }
            expandableContent={
              <div className="mt-10">
                <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 max-w-5xl mx-auto mb-12">
                  {(() => {
                    const roleIcons = [DollarSign, AlertTriangle, Briefcase, Building2, ClipboardCheck];
                    const roleColors = [
                      { bg: 'bg-[#0077FF]/5', iconBg: 'bg-[#0077FF]/10', text: 'text-[#0077FF]', border: 'border-[#0077FF]/20' },
                      { bg: 'bg-amber-50', iconBg: 'bg-amber-100', text: 'text-amber-600', border: 'border-amber-200' },
                      { bg: 'bg-emerald-50', iconBg: 'bg-emerald-100', text: 'text-emerald-600', border: 'border-emerald-200' },
                      { bg: 'bg-violet-50', iconBg: 'bg-violet-100', text: 'text-violet-600', border: 'border-violet-200' },
                      { bg: 'bg-rose-50', iconBg: 'bg-rose-100', text: 'text-rose-600', border: 'border-rose-200' },
                    ];
                    return (t('home.operationalLeaders.roles') || []).map((role, i) => {
                      const Ic = roleIcons[i]; const c = roleColors[i];
                      return (
                        <div key={i} className={`${c.bg} rounded-xl p-5 border ${c.border}`} data-testid={`leader-role-${i}`}>
                          <div className={`w-10 h-10 ${c.iconBg} rounded-lg flex items-center justify-center mb-3`}>
                            <Ic className={`w-5 h-5 ${c.text}`} />
                          </div>
                          <h3 className="text-sm font-bold text-slate-900 mb-1">{role.title}</h3>
                          <p className="text-xs text-slate-500 leading-relaxed">{role.description}</p>
                        </div>
                      );
                    });
                  })()}
                </div>
                <div className="text-center">
                  <p className="text-base text-slate-600 max-w-3xl mx-auto leading-relaxed">{t('home.operationalLeaders.closing')}</p>
                </div>
              </div>
            }
          />
        </div>
      </section>

      {/* Calculator Callout */}
      <section className="py-16 bg-white" data-testid="calculator-callout-section">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <div className="max-w-4xl mx-auto">
            <div className="bg-slate-900 rounded-2xl p-8 md:p-10 flex flex-col md:flex-row items-center gap-8">
              <div className="w-16 h-16 bg-[#FF7F00]/20 rounded-2xl flex items-center justify-center flex-shrink-0">
                <Calculator className="w-8 h-8 text-[#FF7F00]" />
              </div>
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-xl font-bold text-white mb-2" data-testid="calc-callout-title">{t('home.calculatorCallout.title')}</h3>
                <p className="text-slate-400 leading-relaxed">{t('home.calculatorCallout.body')}</p>
              </div>
              <Link
                to={getLocalizedPath('/operational-risk#estimator-section')}
                className="btn-primary bg-[#FF7F00] hover:bg-[#E67200] text-white text-base flex-shrink-0"
                data-testid="calc-callout-btn"
              >
                <Calculator className="w-5 h-5" />
                {t('home.calculatorCallout.btn')}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Customer Case Studies - Hidden placeholder for future use */}
      {/* 
      <section className="section-padding bg-white" data-testid="case-studies-section">
        <div className="container-max">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-caption text-sky-600 mb-4 block">Success Stories</span>
            <h2 className="text-h2 text-slate-900 mb-4">Customer Case Studies</h2>
            <p className="text-body-lg">See how leading organizations use Tapelligence to transform their operations.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[1, 2, 3].map((item) => (
              <div key={item} className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-lg transition-shadow">
                <div className="h-48 bg-slate-100" />
                <div className="p-6">
                  <span className="text-xs font-medium text-sky-600 uppercase tracking-wider">Industry</span>
                  <h3 className="text-lg font-bold text-slate-900 mt-2 mb-3">Case Study Title</h3>
                  <p className="text-sm text-slate-600 mb-4">Brief description of how the client achieved operational excellence.</p>
                  <Link to={getLocalizedPath('/contact')} className="text-sm font-medium text-sky-600 hover:text-sky-700 flex items-center gap-1">
                    Read More <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      */}

      {/* Who Uses Tapelligence */}
      <section className="section-padding bg-white" data-testid="who-uses-section">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6" data-testid="who-uses-title">
                {t('home.whoUses.title')}
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-3">
                {t('home.whoUses.intro')}
              </p>
              <p className="text-lg text-slate-600 leading-relaxed">
                {t('home.whoUses.rolesIntro')}
              </p>
            </div>
            <p className="text-base font-medium text-slate-700 mb-4">{t('home.whoUses.commonRoles')}</p>
            <ul className="space-y-3 mb-8">
              {(t('home.whoUses.roles') || []).map((role, index) => (
                <li key={index} className="flex items-center gap-3" data-testid={`who-uses-role-${index}`}>
                  <div className="w-2 h-2 bg-[#0077FF] rounded-full flex-shrink-0" />
                  <span className="text-slate-700 font-medium">{role}</span>
                </li>
              ))}
            </ul>
            <div className="space-y-3 text-center">
              <p className="text-base text-slate-600 leading-relaxed">
                {t('home.whoUses.environmentLine')}
              </p>
              <p className="text-base font-medium text-slate-800">
                {t('home.whoUses.closing')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-slate-900" data-testid="cta-section">
        <div className="container-max text-center">
          <div className="w-16 h-0.5 bg-gradient-to-r from-[#0077FF] to-[#FF7F00] mx-auto mb-8" />
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to transform your facility operations?
          </h2>
          <p className="text-lg text-slate-300 mb-10 max-w-2xl mx-auto">
            See how Tapelligence provides real-time operational intelligence across all your locations.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to={getLocalizedPath('/contact')} className="btn-primary bg-white text-slate-900 hover:bg-slate-100 text-base" data-testid="final-cta">
              {t('home.hero.cta')}
              <ArrowRight className="w-5 h-5" />
            </Link>
            <a
              href="https://portal.tapelligence.com"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white text-base"
            >
              Access Portal
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
